import { forwardRef, useId, useState } from 'react';
import { ChevronRight } from 'lucide-react';

export interface DisclosureProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function cn(...parts: Array<string | undefined | false | null>): string {
  return parts.filter(Boolean).join(' ');
}

export const Disclosure = forwardRef<HTMLDivElement, DisclosureProps>(function Disclosure(
  { title, defaultOpen = false, className, children, ...rest },
  ref,
) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const disclosureId = useId().replace(/:/g, '');
  const buttonId = `${disclosureId}-trigger`;
  const panelId = `${disclosureId}-panel`;

  const toggle = () => {
    setIsOpen((previous) => !previous);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    toggle();
  };

  return (
    <div
      ref={ref}
      className={cn(
        'rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface)]',
        className,
      )}
      {...rest}
    >
      <button
        id={buttonId}
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        className="flex w-full items-center justify-between gap-3 p-4 text-left hover:bg-[color:var(--color-hover-overlay)]"
      >
        <span className="flex min-w-0 items-center gap-2">
          <ChevronRight
            aria-hidden="true"
            className={cn(
              'h-4 w-4 text-[color:var(--color-text-muted)] motion-safe:transition-transform motion-safe:duration-150',
              isOpen && 'rotate-90',
            )}
          />
          <span className="text-base font-semibold text-[color:var(--color-text-primary)]">{title}</span>
        </span>
      </button>

      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!isOpen}
        className="border-t border-[color:var(--color-border-subtle)] px-4 pb-4 pt-0"
      >
        <div className="pt-4">{children}</div>
      </div>
    </div>
  );
});
